import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { blogAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePWHLPlayers } from '../../hooks/usePWHLPlayers';
import { linkifyPlayers } from '../../utils/linkifyPlayers';
import '../../css/blog.css';

// Static blog data as fallback
const staticPosts = [
  {
    id: 'static-1',
    slug: 'music-genre-classification-machine-learning',
    title: 'Classifying Music Genres with a CNN',
    excerpt: 'A deep dive into using convolutional neural networks to classify music genres through spectrogram analysis, achieving 83% validation accuracy on 9,000 songs across 9 genres.',
    body: `
      <div style="margin-bottom: 30px;">
        <iframe width="100%" style="aspect-ratio: 16/9; border-radius: 8px;"
          src="https://www.youtube.com/embed/uCnjrpbmTC0?autoplay=1&mute=1&rel=0"
          title="Classifying Music Genres with a CNN" frameborder="0" allowfullscreen></iframe>
      </div>
      <h2>Abstract</h2>
      <p>This project addresses the challenge of automating music genre classification, which is essential for organizing music libraries and enabling music discovery services. While manual classification proves labor-intensive and subjective, machine learning offers scalable alternatives. The research compares two approaches: analyzing extracted audio features through traditional classifiers versus processing spectrogram images through convolutional neural networks (CNN). The study emphasizes CNN advantages in eliminating the need for prior feature engineering and dataset labeling.</p>

      <h2>1 Introduction</h2>
      <p>This initiative develops a convolutional neural network achieving superior accuracy compared to baseline predictions. Prior research established a 63% accuracy ceiling using support vector machines with engineered audio features. The project leverages a dataset exceeding 100,000 songs in MP3 format, each accompanied by 518 engineered audio feature measurements. The innovation involves processing spectrogram representations rather than tabular feature data.</p>

      <h3>1.1 Benchmark Analysis on Audio Features</h3>
      <p>Multiple classification models underwent evaluation: Logistic Regression, k-Nearest-Neighbors, Decision Trees, Multilayer Perceptron Neural Networks, and Support Vector Machines with various kernels. The optimal result utilized an RBF kernel SVM and the following audio features: mel-frequency cepstrum coefficients (MFCC), contrast, and spectral centroid achieving 63% accuracy.</p>

      <h3>1.2 Featureless Analysis with a CNN</h3>
      <p>Convolutional neural networks process image inputs through learnable weights and biases, updating through backpropagation. Unlike traditional neural networks accepting tabular data, CNNs require visual representations of audio files for genre classification.</p>

      <h2>2 Audio Signal Processing</h2>
      <p>The transformation pipeline converts audio files through sequential stages:</p>
      <ol>
          <li>MPEG Audio Layer-3 (.mp3)</li>
          <li>Waveform Audio File Format (.wav)</li>
          <li>Waveform Image: Amplitude vs. Time (.png)</li>
          <li>Periodogram: Magnitude vs. Frequency (.png)</li>
          <li>Spectrogram: Frequency vs. Time (.png)</li>
          <li>Mel Spectrogram: Mel Scale Frequency vs. Time (.png)</li>
      </ol>

      <p>The Waveform Audio File Format represents audio as digital bitstreams sampled at rates like 44.1kHz (44,100 samples per second). While waveform images appear visually similar across genres, they lack distinctive patterns for machine learning classification.</p>

      <p>The Fourier transformation decomposes signals into component frequencies and amplitudes, converting time-domain data into frequency-domain representations called spectra. This mathematical process identifies individual sine and cosine waves comprising the original signal.</p>

      <p>The fast Fourier transform (FFT) algorithm enables frequency analysis of non-stationary signals through windowed segments, producing spectrograms displaying loudness variations across frequencies and time. Logarithmic scaling on both axes accommodates human auditory perception limitations.</p>

      <p>The mel scale, introduced in 1937, models human pitch perception more accurately than linear frequency. Humans detect lower-frequency differences more readily than higher ones. Mel spectrograms apply this perceptual scale to improve classification accuracy.</p>

      <h2>3 Related Work</h2>
      <p>The Free Music Archive (FMA) dataset, released in 2017, provides 100,000+ songs with extracted audio features. Prior research established a 63% accuracy benchmark using support vector machines but proposed neural networks analyzing waveforms as potential improvements.</p>

      <p>Subsequent work demonstrated CNN effectiveness for artist identification from spectrograms, achieving approximately 72% test accuracy. This success indicated CNN potential for genre classification with parameter optimization.</p>

      <h2>4 Results</h2>

      <h3>4.1 Benchmark Results</h3>
      <p>Feature analysis revealed that MFCC, contrast, and spectral centroid generated optimal results. MFCC describes spectral envelope characteristics, contrast measures foreground-background noise differentiation, and spectral centroid indicates spectrum mass distribution.</p>

      <p>The larger dataset produced higher prediction accuracies, matching the FMA research paper's 63% maximum using support vector machines. This matching result validated the experimental methodology.</p>

      <h3>4.2 CNN Results</h3>
      <p>The CNN architecture comprised 6 convolutional layers, dropout regularization, and a SoftMax output layer. Training parameters included:</p>
      <ul>
          <li>100 training epochs</li>
          <li>Learning rate: 0.00005</li>
          <li>Dropout rate: 30%</li>
          <li>Kernel size: 3x3</li>
          <li>9 genre classifications</li>
          <li>Dataset: 9,000 songs (1,000 per genre)</li>
          <li>Train-test split: 90-10</li>
      </ul>

      <p>Results achieved 99.7% training accuracy and 83% validation accuracy. The performance gap suggests modest overfitting, potentially attributable to the relatively small test dataset variance.</p>

      <h2>5 Web Application and Analysis of Classifications</h2>
      <p>A demonstration application processes user-uploaded MP3 files through the complete pipeline, generating mel spectrograms and outputting SoftMax probability distributions across genres alongside visual representations.</p>

      <p>Empirical testing suggested approximately 80% correct classification rates across songs tested. Misclassifications frequently occurred with genre-boundary songs, exemplified by the rock composition receiving metal classification due to vocal intensity similarities.</p>

      <h3>5.1 Thinking Like a Machine</h3>
      <p>CNNs identify patterns through pixel-edge contrast analysis. Spectrogram comparisons revealed distinct visual differences between genres, though boundary-case songs displayed spectral characteristics resembling neighboring genres. This suggests the model operates as an unbiased spectral analyzer without cultural or commercial bias.</p>

      <h2>6 Areas of Improvement</h2>
      <p>Identified challenges include:</p>
      <ul>
          <li>Louder songs receiving metal classifications</li>
          <li>Subgenre distinctions (old vs. new country) requiring additional classification categories</li>
          <li>Overfitting potentially reduced through cross-validation optimization</li>
          <li>Expanded training datasets enabling improved boundary classification</li>
      </ul>

      <h2>7 Conclusion</h2>
      <p>CNN-based classification demonstrated superior performance compared to feature-extraction approaches using equivalent or smaller datasets. While misclassifications occur in genre-boundary territories, the model functions as an objective spectrogram analyzer. Future improvements require expanded datasets, architectural refinements, and finer genre categorization. The method preserves computational efficiency by eliminating feature storage requirements.</p>

      <h2>References</h2>
      <ol>
          <li>Michaël Defferrard, Kirell Benzi, Pierre Vandergheynst, Xavier Bresson. 2017. "FMA: A Dataset for Music Analysis"</li>
          <li>Librosa – Audio and Music processing in Python Library</li>
          <li>Leland Roberts. 2020. "Understanding the Mel Spectrogram"</li>
          <li>Jiyoung Park, Jongpil Lee, Jangyeon Park, Jung-Woo Ha, Juhan Nam. 2018. "Representation Learning of Music Using Artist Labels"</li>
          <li>Keras deep learning API Python Library</li>
          <li>Streamlit – easy web apps for data science Python Library</li>
          <li>Scikit-learn SVM Classifier</li>
          <li>Benjamin Murauer, Günther Specht. 2018. "Detecting Music Genre Using Extreme Gradient Boosting"</li>
          <li>Seth Adams. 2018. "DSP Background – Deep Learning for Audio Classification"</li>
      </ol>

      <p><strong>Source code:</strong> <a href="https://github.com/ericzacharia/MusicGenreClassifier" target="_blank" rel="noopener noreferrer" style="color: var(--pink);">github.com/ericzacharia/MusicGenreClassifier</a></p>
    `,
    author: 'Eric Zacharia',
    published_at: 'March 15, 2024',
    reading_time: 12
  },
  {
    id: 'static-2',
    slug: 'stock-market-prediction-ai',
    title: 'Stock Sentiment Trader',
    excerpt: 'An automated trading system using BERT-based NLP to analyze Twitter sentiment and execute trades via Alpaca API, achieving 1.8% returns vs S&P 500\'s 0.85%.',
    body: `
      <div style="margin-bottom: 30px;">
        <iframe width="100%" style="aspect-ratio: 16/9; border-radius: 8px;"
          src="https://www.youtube.com/embed/ZJDqm7mhDfI?autoplay=1&mute=1&rel=0"
          title="Stock Sentiment Trader" frameborder="0" allowfullscreen></iframe>
      </div>
      <h2>Project Overview</h2>
      <p>This project presents an automated trading system that leverages natural language processing to analyze Twitter sentiment regarding stocks. The system employs a BERT-based transformer model trained on 5,790 labeled tweets to classify market opinions as bullish or bearish, then executes trades through the Alpaca API based on aggregated sentiment signals.</p>

      <h2>Sentiment Classification Model</h2>
      <p>The implementation uses an altered BERT model trained on Twitter data. After 10 epochs, the model achieved approximately 83% accuracy on validation data. The system computes likelihood scores for both sentiment categories, with the difference between scores representing prediction certainty.</p>

      <h3>Training Details</h3>
      <ul>
          <li>Dataset: 5,790 labeled tweets</li>
          <li>Model: BERT-based transformer</li>
          <li>Training epochs: 10</li>
          <li>Validation accuracy: ~83%</li>
          <li>Output: Bullish vs. Bearish classification with confidence scores</li>
      </ul>

      <h2>Why Twitter Over Traditional News?</h2>
      <p>The system prioritizes Twitter data over traditional news sources for several key reasons:</p>
      <ul>
          <li><strong>Conciseness:</strong> Tweets are short, reducing computation time compared to lengthy news articles</li>
          <li><strong>Real-time Commentary:</strong> Twitter provides immediate market reactions during trading hours</li>
          <li><strong>Bypass Clickbait:</strong> Tweet format avoids misleading headlines and sensationalism</li>
          <li><strong>Market Impact:</strong> Social media can drive immediate market movements</li>
      </ul>

      <p>A notable 2013 example illustrates this power: a fraudulent AP tweet claiming White House explosions triggered an immediate market sell-off, demonstrating how social media sentiment directly impacts stock prices in real-time.</p>

      <h2>Trading Pipeline Architecture</h2>
      <p>The automated system follows this workflow:</p>
      <ol>
          <li><strong>Query Phase:</strong> Cycles through targeted stock symbols</li>
          <li><strong>Data Collection:</strong> Gathers recent tweets mentioning each stock</li>
          <li><strong>Preprocessing:</strong> Cleans tweet text using regular expressions</li>
          <li><strong>Sentiment Analysis:</strong> BERT model generates bullish/bearish predictions with confidence scores</li>
          <li><strong>Risk Assessment:</strong> Applies adjustable risk thresholds to filter low-confidence signals</li>
          <li><strong>Position Sizing:</strong> Calculates trade sizes based on sentiment polarity and available portfolio equity</li>
          <li><strong>Execution:</strong> Places trades through Alpaca API</li>
      </ol>

      <h2>Performance Results</h2>
      <p>On August 25, 2021, the trading bot demonstrated promising performance:</p>
      <ul>
          <li><strong>Bot Return:</strong> 1.8%</li>
          <li><strong>S&P 500 Return:</strong> 0.85%</li>
          <li><strong>Outperformance:</strong> 2.12x market return</li>
      </ul>

      <p><strong>Portfolio Concentration:</strong> The system concentrated holdings in NVDA, AMD, and TSLA—stocks that performed strongest that day. This suggests the sentiment analysis successfully identified which stocks would outperform, validating the approach of using social media sentiment for stock selection.</p>

      <h2>System Limitations</h2>
      <p>While showing promise, the system has several acknowledged constraints:</p>
      <ul>
          <li><strong>Small Training Dataset:</strong> Only 5,790 tweets limits model generalization</li>
          <li><strong>No Diversification Strategy:</strong> Risk management could be enhanced with portfolio constraints</li>
          <li><strong>Delayed Response:</strong> 2-minute analysis cycle creates lag between sentiment shifts and trades</li>
          <li><strong>Single Signal Source:</strong> Lacks integration with price trends and technical indicators</li>
          <li><strong>Model Accuracy:</strong> 83% accuracy leaves 17% error rate, representing substantial room for improvement</li>
          <li><strong>Market Hours Dependency:</strong> System only operates during market hours, missing after-hours sentiment</li>
      </ul>

      <h2>Technical Implementation</h2>
      <p>The system integrates several key technologies:</p>
      <ul>
          <li><strong>NLP Framework:</strong> BERT transformer for sentiment classification</li>
          <li><strong>Trading API:</strong> Alpaca for order execution</li>
          <li><strong>Data Source:</strong> Twitter API for real-time social media data</li>
          <li><strong>Text Processing:</strong> Regular expressions for data cleaning</li>
          <li><strong>Risk Management:</strong> Configurable confidence thresholds</li>
      </ul>

      <h2>Future Enhancements</h2>
      <p>Potential improvements to increase system performance:</p>
      <ul>
          <li>Expand training dataset beyond 5,790 tweets</li>
          <li>Implement portfolio diversification rules</li>
          <li>Reduce analysis cycle from 2 minutes to real-time</li>
          <li>Integrate technical analysis and price trends</li>
          <li>Improve model architecture for higher than 83% accuracy</li>
          <li>Add after-hours sentiment monitoring</li>
          <li>Implement stop-loss and take-profit mechanisms</li>
      </ul>

      <h2>Conclusion</h2>
      <p>This project demonstrates that social media sentiment analysis can provide actionable trading signals, outperforming market benchmarks on test days. The system's ability to concentrate positions in the day's top performers (NVDA, AMD, TSLA) validates the core hypothesis that Twitter sentiment correlates with short-term stock performance.</p>

      <p>However, the acknowledged limitations—particularly the small training dataset and lack of diversification—suggest this represents a promising proof-of-concept rather than production-ready trading system. Future work should focus on expanding the dataset, improving model accuracy beyond 83%, and integrating additional signal sources for more robust decision-making.</p>
    `,
    author: 'Eric Zacharia',
    published_at: 'April 10, 2024',
    reading_time: 10
  },
  {
    id: 'static-3',
    slug: 'understanding-ai-legal-research',
    title: 'Understanding AI in Legal Research',
    excerpt: 'Discover how artificial intelligence is revolutionizing legal research, making it faster and more accurate than ever before.',
    body: `
      <h2>AI Revolution in Legal Research</h2>
      <p>Artificial intelligence is transforming the legal industry, particularly in the domain of legal research. AI-powered tools can now analyze thousands of cases in seconds, identify relevant precedents, and provide insights that would take human researchers days or weeks to uncover.</p>

      <h3>Natural Language Processing in Law</h3>
      <p>Modern legal AI systems use advanced NLP techniques to understand legal language, which differs significantly from everyday English. These systems can parse complex legal documents, understand citations, and identify relationships between cases.</p>

      <h3>Key Benefits</h3>
      <ul>
        <li><strong>Speed:</strong> AI can search through millions of documents in seconds</li>
        <li><strong>Accuracy:</strong> Machine learning models identify relevant cases with high precision</li>
        <li><strong>Cost Efficiency:</strong> Reduces billable hours for routine research tasks</li>
        <li><strong>Comprehensive Coverage:</strong> Never misses a relevant precedent</li>
      </ul>

      <h3>Current Limitations</h3>
      <p>While AI excels at finding and analyzing documents, human judgment remains essential for legal strategy, argumentation, and client interaction. AI should be viewed as an augmentation tool rather than a replacement for legal professionals.</p>

      <h3>The Future</h3>
      <p>As AI technology continues to evolve, we expect to see even more sophisticated legal research tools that can understand context, predict outcomes, and provide strategic recommendations.</p>
    `,
    author: 'UnsupervisedBias',
    published_at: 'June 20, 2024',
    reading_time: 6
  }
];

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPdub = searchParams.get('ref') === 'pdubfantasy';
  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const { players } = usePWHLPlayers();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [useStaticData, setUseStaticData] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await blogAPI.getPostBySlug(slug);
        setPost(response.data);
        setUseStaticData(false);
      } catch (error) {
        console.error('Error fetching post from API:', error);
        // Fallback to static data
        const staticPost = staticPosts.find(p => p.slug === slug);
        if (staticPost) {
          setPost(staticPost);
          setUseStaticData(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  useEffect(() => {
    const fetchComments = async () => {
      if (!post || !post.id) return;
      try {
        const response = await blogAPI.getComments(post.id);
        const commentsData = response.data.results || response.data;
        setComments(Array.isArray(commentsData) ? commentsData : []);
      } catch (error) {
        console.error('Error fetching comments:', error);
        setComments([]);
      }
    };
    fetchComments();
  }, [post]);

  // Manage <link rel="canonical"> for the post. When the same article was
  // crossposted to pdubfantasy.com, point Google at the pdub version so SEO
  // signals consolidate there.
  useEffect(() => {
    if (!post || !post.slug) return;
    const href = post.crossposted_to_pdub
      ? `https://pdubfantasy.com/articles/${post.slug}`
      : `https://unsupervisedbias.com/blog/${post.slug}/`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
    return () => {
      if (link && link.parentNode) link.parentNode.removeChild(link);
    };
  }, [post]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate('/signin', {
        state: {
          from: `/blog/${slug}`,
          message: 'Please sign in to leave a comment.'
        }
      });
      return;
    }

    if (!post.id || useStaticData) {
      showToast('Comments are not available for this blog post yet.', 'error');
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await blogAPI.createComment({
        post: post.id,
        content: newComment
      });

      // Refresh comments to get updated data with like counts
      const commentsResponse = await blogAPI.getComments(post.id);
      const commentsData = commentsResponse.data.results || commentsResponse.data;
      setComments(Array.isArray(commentsData) ? commentsData : []);
      setNewComment('');
      showToast('Comment submitted successfully!', 'success');
    } catch (error) {
      console.error('Error submitting comment:', error);
      showToast('Failed to submit comment. Please try again.', 'error');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!isAuthenticated) {
      navigate('/signin', {
        state: {
          from: `/blog/${slug}`,
          message: 'Please sign in to like comments.'
        }
      });
      return;
    }

    try {
      await blogAPI.toggleCommentLike(commentId);
      // Refresh comments to get updated like counts
      const response = await blogAPI.getComments(post.id);
      const commentsData = response.data.results || response.data;
      setComments(Array.isArray(commentsData) ? commentsData : []);
    } catch (error) {
      console.error('Error toggling like:', error);
      showToast('Failed to like comment. Please try again.', 'error');
    }
  };

  const handleReplyClick = (commentId) => {
    if (!isAuthenticated) {
      navigate('/signin', {
        state: {
          from: `/blog/${slug}`,
          message: 'Please sign in to reply to comments.'
        }
      });
      return;
    }
    setReplyingTo(replyingTo === commentId ? null : commentId);
    setReplyContent('');
  };

  const handleReplySubmit = async (parentCommentId) => {
    if (!replyContent.trim()) {
      showToast('Please enter a reply.', 'error');
      return;
    }

    try {
      await blogAPI.createComment({
        post: post.id,
        parent: parentCommentId,
        content: replyContent
      });

      // Refresh comments to get updated data
      const response = await blogAPI.getComments(post.id);
      const commentsData = response.data.results || response.data;
      setComments(Array.isArray(commentsData) ? commentsData : []);
      setReplyContent('');
      setReplyingTo(null);
      showToast('Reply submitted successfully!', 'success');
    } catch (error) {
      console.error('Error submitting reply:', error);
      showToast('Failed to submit reply. Please try again.', 'error');
    }
  };

  const renderComment = (comment, depth = 0) => (
    <div
      key={comment.id}
      style={{
        padding: '1.5rem',
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        marginBottom: '1rem'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {comment.user?.profile_image ? (
            <img
              src={comment.user.profile_image}
              alt={`${comment.user?.first_name || 'User'} avatar`}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                objectFit: 'cover',
                marginRight: '1rem',
                border: '2px solid var(--violet)'
              }}
            />
          ) : (
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'var(--violet)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '1rem',
                fontWeight: '600',
                fontSize: '16px'
              }}
            >
              {comment.user?.first_name?.charAt(0) || comment.user?.email?.charAt(0) || '?'}
            </div>
          )}
          <div>
            <div style={{ fontWeight: '600', color: 'white' }}>
              {comment.user?.first_name && comment.user?.last_name
                ? `${comment.user.first_name} ${comment.user.last_name}`
                : comment.user?.email || 'Anonymous'}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
              {new Date(comment.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              {comment.is_edited && <span style={{ marginLeft: '8px' }}>(edited)</span>}
            </div>
          </div>
        </div>
      </div>
      <p style={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.6', margin: '0 0 1rem 0' }}>
        {comment.content}
      </p>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <button
          onClick={() => handleLikeComment(comment.id)}
          style={{
            background: 'transparent',
            border: 'none',
            color: comment.user_has_liked ? 'var(--pink)' : 'rgba(255, 255, 255, 0.6)',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <span style={{ fontSize: '16px' }}>{comment.user_has_liked ? '❤️' : '🤍'}</span>
          <span>{comment.like_count || 0}</span>
        </button>

        <button
          onClick={() => handleReplyClick(comment.id)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.6)',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '0.5rem',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          💬 Reply
        </button>
      </div>

      {/* Reply form */}
      {replyingTo === comment.id && (
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Write your reply..."
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '0.75rem',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button
              onClick={() => handleReplySubmit(comment.id)}
              disabled={!replyContent.trim()}
              style={{
                padding: '0.5rem 1.5rem',
                background: 'var(--pink)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: replyContent.trim() ? 'pointer' : 'not-allowed',
                opacity: replyContent.trim() ? 1 : 0.5
              }}
            >
              Submit Reply
            </button>
            <button
              onClick={() => {
                setReplyingTo(null);
                setReplyContent('');
              }}
              style={{
                padding: '0.5rem 1.5rem',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Nested Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div style={{ marginLeft: '2rem', marginTop: '1rem' }}>
          {comment.replies.map((reply) => renderComment(reply, depth + 1))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="blog-page">
        <div className="container">
          <p style={{ textAlign: 'center', padding: '4rem 0', color: 'rgba(255, 255, 255, 0.7)' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-page">
        <div className="container">
          <p style={{ textAlign: 'center', padding: '4rem 0', color: 'rgba(255, 255, 255, 0.7)' }}>
            Blog post not found.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <Link to="/blog" style={{ color: 'var(--pink)', textDecoration: 'none' }}>
              ← Back to Blog
            </Link>
            {fromPdub && (
              <a href="https://pdubfantasy.com" style={{ color: 'var(--pink)', textDecoration: 'none', opacity: 0.7 }}>
                ← Back to pdubfantasy
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <Link to="/blog" style={{ color: 'var(--pink)', textDecoration: 'none' }}>
            ← Back to Blog
          </Link>
          {fromPdub && (
            <a href="https://pdubfantasy.com" style={{ color: 'var(--pink)', textDecoration: 'none', opacity: 0.7 }}>
              ← Back to pdubfantasy
            </a>
          )}
        </div>

        <article style={{ marginTop: '2rem' }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: 'white' }}>{post.title}</h1>

          {post.excerpt && (
            <p style={{ fontSize: '1.2rem', color: 'rgba(255, 255, 255, 0.8)', marginBottom: '2rem' }}>
              {post.excerpt}
            </p>
          )}

          <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '2rem' }}>
            Published on {post.published_date
              ? new Date(post.published_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
              : post.published_at} by <strong>
              {post.author
                ? (typeof post.author === 'string'
                    ? post.author
                    : `${post.author.first_name || ''} ${post.author.last_name || ''}`.trim() || post.author.email || 'Anonymous')
                : 'UnsupervisedBias'}
            </strong>
          </p>
          {post.featured_image && (
            <img src={post.featured_image} alt={post.title} style={{ width: '100%', marginBottom: '2rem' }} />
          )}
          <div
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: linkifyPlayers(post.content || post.body || 'Content coming soon...', players) }}
          />

          {/* Comments Section */}
          <div className="comments-section" style={{
            marginTop: '4rem',
            padding: '2rem',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h2 style={{ color: 'var(--pink)', marginBottom: '2rem' }}>
              Comments ({comments.length})
            </h2>

            {/* Comment Form */}
            <div className="comment-form" style={{ marginBottom: '3rem' }}>
              {useStaticData ? (
                <div style={{
                  padding: '2rem',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center'
                }}>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Comments are not available for this blog post yet.
                  </p>
                </div>
              ) : user ? (
                <form onSubmit={handleCommentSubmit}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts..."
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '1rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={submittingComment || !newComment.trim()}
                    style={{
                      marginTop: '1rem',
                      padding: '0.75rem 2rem',
                      background: 'var(--pink)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: submittingComment || !newComment.trim() ? 'not-allowed' : 'pointer',
                      opacity: submittingComment || !newComment.trim() ? 0.5 : 1
                    }}
                  >
                    {submittingComment ? 'Submitting...' : 'Post Comment'}
                  </button>
                </form>
              ) : (
                <div style={{
                  padding: '2rem',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center'
                }}>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem' }}>
                    Please sign in to leave a comment
                  </p>
                  <Link
                    to="/signin"
                    style={{
                      display: 'inline-block',
                      padding: '0.75rem 2rem',
                      background: 'var(--pink)',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Sign In
                  </Link>
                </div>
              )}
            </div>

            {/* Comments List */}
            <div className="comments-list">
              {comments.length === 0 ? (
                <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', padding: '2rem' }}>
                  No comments yet. Be the first to comment!
                </p>
              ) : (
                comments.map((comment) => renderComment(comment))
              )}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
